import React, { useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { HocuspocusProvider } from '@hocuspocus/provider';
import { IndexeddbPersistence } from 'y-indexeddb';
import { clearLocalDocumentCacheIfRestored, markDocumentRestoredForReload } from './clear-local-document-cache.js';
import { createLiveYDoc } from './create-live-ydoc';
import { decodeDocumentYjsContent } from './decode-document-yjs-content';
import { useEditor as useTipTapEditor } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import Document from '@tiptap/extension-document';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { ListKit } from '@tiptap/extension-list';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import ImageResize from 'tiptap-extension-resize-image';
import { Footnotes, Footnote, FootnoteReference } from 'tiptap-footnotes';
import { HighlightColor } from './highlight-color';
import { SuggestionInsertMarkers } from './suggestion-insert-markers';
import { HyphenToDash } from './hyphen-to-dash';
import { CollapseSpaces } from './collapse-spaces';
import { AutomaticReplacements } from './automatic-replacements';
import { ScriptMode } from './script-mode';
import { configureScriptMention } from './script-mention';
import { FileHandler } from './file-handler';
import { SearchHighlightExtension } from './search-highlight';
import { createConnectionErrorReporter } from './connection-error-reporter.js';
import { COLLABORATION_CARET_LABEL_CLASS, mutationsAffectCollaborationCaretLabels, } from './collaboration-caret-label-mutations.js';
import { GatedBulletList, GatedOrderedList, GatedTaskItem, } from './gated-list-input-rules';
const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if (0 <= h && h < 60) {
        r = c;
        g = x;
        b = 0;
    }
    else if (60 <= h && h < 120) {
        r = x;
        g = c;
        b = 0;
    }
    else if (120 <= h && h < 180) {
        r = 0;
        g = c;
        b = x;
    }
    else if (180 <= h && h < 240) {
        r = 0;
        g = x;
        b = c;
    }
    else if (240 <= h && h < 300) {
        r = x;
        g = 0;
        b = c;
    }
    else if (300 <= h && h < 360) {
        r = c;
        g = 0;
        b = x;
    }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};
const normalizeColorToHex = (color) => {
    if (color.startsWith('#')) {
        return color;
    }
    if (color.startsWith('rgb') || color.startsWith('rgba')) {
        const rgbMatch = color.match(/\d+/g);
        if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        }
    }
    if (color.startsWith('hsl')) {
        const hslMatch = color.match(/\d+/g);
        if (hslMatch && hslMatch.length >= 3) {
            const h = parseInt(hslMatch[0], 10);
            const s = parseInt(hslMatch[1], 10);
            const l = parseInt(hslMatch[2], 10);
            return hslToHex(h, s, l);
        }
    }
    return color;
};
const generateColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return hslToHex(hue, 70, 50);
};
const getTextColorForBackground = (backgroundColor) => {
    if (typeof document === 'undefined') {
        return '#0d0d0d';
    }
    const tempDiv = document.createElement('div');
    tempDiv.style.backgroundColor = backgroundColor;
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).backgroundColor;
    document.body.removeChild(tempDiv);
    const rgbMatch = computedColor.match(/\d+/g);
    if (!rgbMatch || rgbMatch.length < 3) {
        if (backgroundColor.startsWith('hsl')) {
            const hslMatch = backgroundColor.match(/\d+/g);
            if (hslMatch && hslMatch.length >= 3) {
                const lightness = parseInt(hslMatch[2], 10);
                return lightness > 50 ? '#0d0d0d' : '#ffffff';
            }
        }
        return '#0d0d0d';
    }
    const r = parseInt(rgbMatch[0], 10);
    const g = parseInt(rgbMatch[1], 10);
    const b = parseInt(rgbMatch[2], 10);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#0d0d0d' : '#ffffff';
};
const createBaseExtensions = (headingExtension, getEnableListsSetting) => [
    StarterKit.configure({
        bulletList: false,
        listItem: false,
        orderedList: false,
        link: false,
        undoRedo: false,
        document: false,
        heading: false,
    }),
    headingExtension ?? Heading,
    Document.extend({
        content: 'block+ footnotes?',
    }),
    Link.configure({
        openOnClick: true,
        HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
        },
    }),
    Image.configure({
        HTMLAttributes: {
            class: 'max-w-full h-auto',
        },
    }),
    ImageResize.configure({
        inline: true,
    }),
    ListKit.configure({
        bulletList: false,
        orderedList: false,
        taskItem: false,
        taskList: {
            HTMLAttributes: {
                class: 'task-list',
            },
        },
    }),
    GatedBulletList.configure({
        HTMLAttributes: { class: 'list-disc' },
        getEnableListsSetting,
    }),
    GatedOrderedList.configure({
        HTMLAttributes: { class: 'list-decimal' },
        getEnableListsSetting,
    }),
    GatedTaskItem.configure({
        nested: true,
        HTMLAttributes: {
            class: 'task-item',
        },
        getEnableListsSetting,
    }),
    Footnotes,
    Footnote,
    FootnoteReference,
];
const STALE_TAB_THRESHOLD_MS = 30 * 60 * 1000;
export const useCollaborativeEditor = ({ documentId, token, apiBase, websocketUrl = 'ws://localhost:1234', masterKey, userName, userColor, editable = true, shareToken, getReplaceHyphensWithDashesSetting, getCollapseConsecutiveSpacesSetting, getAutomaticReplacementsSetting, getEnableListsSetting, getEnableScriptModeSetting, getScriptMentionItems, getIsScriptModeActive, getPreferEntityMentions, getPlaceholderText, handleImageFiles, headingExtension, entityMentionExtension, extraExtensions, onStateless }) => {
    const [isDocumentLoaded, setIsDocumentLoaded] = React.useState(documentId === 'new');
    const [isConnected, setIsConnected] = React.useState(false);
    const [connectionError, setConnectionError] = React.useState(null);
    const [provider, setProvider] = React.useState(null);
    const [editorKey, setEditorKey] = React.useState(0);
    const [contentLoaded, setContentLoaded] = React.useState(documentId === 'new');
    const ydocRef = useRef(null);
    const documentIdRef = useRef(null);
    const lastDisconnectTimeRef = useRef(null);
    const indexeddbPersistenceRef = useRef(null);
    const onStatelessRef = useRef(onStateless);
    onStatelessRef.current = onStateless;
    if (documentIdRef.current !== documentId) {
        if (indexeddbPersistenceRef.current) {
            indexeddbPersistenceRef.current.destroy();
            indexeddbPersistenceRef.current = null;
        }
        if (ydocRef.current && !ydocRef.current.isDestroyed) {
            ydocRef.current.destroy();
        }
        ydocRef.current = createLiveYDoc();
        documentIdRef.current = documentId;
    }
    const ydoc = ydocRef.current;
    useEffect(() => {
        if (!ydoc || documentId === 'new' || typeof window === 'undefined') {
            return;
        }
        let cancelled = false;
        let persistence = null;
        const setupPersistence = async () => {
            await clearLocalDocumentCacheIfRestored(documentId);
            if (cancelled) {
                return;
            }
            persistence = new IndexeddbPersistence(documentId, ydoc);
            persistence.on('synced', () => {
                console.log('[IndexedDB] Document synced to local storage');
            });
            indexeddbPersistenceRef.current = persistence;
        };
        void setupPersistence();
        return () => {
            cancelled = true;
            if (indexeddbPersistenceRef.current) {
                indexeddbPersistenceRef.current.destroy();
                indexeddbPersistenceRef.current = null;
            }
            else if (persistence) {
                persistence.destroy();
            }
        };
    }, [ydoc, documentId]);
    const providerRef = useRef(null);
    const missingCaretRetryRef = useRef(false);
    const [contentLoadGeneration, setContentLoadGeneration] = React.useState(0);
    useEffect(() => {
        setIsDocumentLoaded(documentId === 'new');
        setIsConnected(false);
        setConnectionError(null);
        setContentLoaded(documentId === 'new');
        missingCaretRetryRef.current = false;
    }, [documentId]);
    useEffect(() => {
        const loadDocument = async () => {
            const currentYdoc = ydocRef.current;
            if (documentId === 'new' || !documentId) {
                setContentLoaded(true);
                return;
            }
            if ((!token && !masterKey && !shareToken) || !currentYdoc) {
                return;
            }
            try {
                if (typeof window !== 'undefined' && indexeddbPersistenceRef.current) {
                    await new Promise((resolve) => {
                        const persistence = indexeddbPersistenceRef.current;
                        if (!persistence) {
                            resolve();
                            return;
                        }
                        const checkSynced = () => {
                            if (persistence.synced) {
                                persistence.off('synced', checkSynced);
                                resolve();
                            }
                        };
                        if (persistence.synced) {
                            resolve();
                        }
                        else {
                            persistence.on('synced', checkSynced);
                            setTimeout(() => {
                                persistence.off('synced', checkSynced);
                                resolve();
                            }, 1000);
                        }
                    });
                }
                let url;
                let headers = {};
                if (shareToken) {
                    url = `${apiBase}/api/share-links/${shareToken}/document`;
                }
                else if (masterKey) {
                    url = `${apiBase}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`;
                }
                else {
                    url = `${apiBase}/api/documents/${documentId}`;
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }
                }
                let loadedFromServer = false;
                if (navigator.onLine) {
                    try {
                        const response = await fetch(url, { headers });
                        if (response.ok) {
                            const doc = await response.json();
                            const contentUint8Array = decodeDocumentYjsContent(doc);
                            if (contentUint8Array) {
                                Y.applyUpdate(currentYdoc, contentUint8Array);
                                loadedFromServer = true;
                            }
                        }
                    }
                    catch (fetchError) {
                        console.warn('[Document Load] Failed to fetch from server, using IndexedDB cache:', fetchError);
                    }
                }
                const defaultFragment = currentYdoc.getXmlFragment('default');
                if (defaultFragment.length === 0 && !loadedFromServer) {
                    const prosemirrorFragment = currentYdoc.getXmlFragment('prosemirror');
                    const contentFragment = currentYdoc.getXmlFragment('content');
                    const emptyFragment = currentYdoc.getXmlFragment('');
                    if (prosemirrorFragment.length === 0 && contentFragment.length === 0 && emptyFragment.length === 0) {
                        if (!navigator.onLine) {
                            console.warn('[Document Load] Offline and no cached content found');
                        }
                        else {
                            console.warn(`[Document Load] Y.Doc 'default' fragment is empty. Checking other fragments:`, {
                                prosemirror: prosemirrorFragment.length,
                                content: contentFragment.length,
                                empty: emptyFragment.length
                            });
                        }
                    }
                }
                setContentLoaded(true);
            }
            catch (error) {
                console.error('Error loading document:', error);
                if (error instanceof Error) {
                    setConnectionError(error.message);
                }
                else if (typeof error === 'string') {
                    setConnectionError(error);
                }
                else {
                    setConnectionError('Failed to load document');
                }
                setContentLoaded(true);
            }
        };
        loadDocument();
    }, [documentId, token, masterKey, shareToken, apiBase, contentLoadGeneration]);
    useEffect(() => {
        if ((!token && !masterKey) || documentId === 'new' || !contentLoaded) {
            if (documentId === 'new') {
                setIsDocumentLoaded(true);
                setIsConnected(false);
            }
            return;
        }
        const currentYdoc = ydocRef.current;
        if (!currentYdoc || currentYdoc.isDestroyed) {
            return;
        }
        const currentToken = masterKey || token;
        if (!currentToken) {
            return;
        }
        setIsDocumentLoaded(false);
        setIsConnected(false);
        setConnectionError(null);
        const newProvider = new HocuspocusProvider({
            url: websocketUrl,
            name: documentId,
            document: currentYdoc,
            token: currentToken,
        });
        providerRef.current = newProvider;
        setProvider(newProvider);
        let loadedRef = false;
        let disposed = false;
        const connectionErrorReporter = createConnectionErrorReporter(setConnectionError);
        const handleSynced = () => {
            loadedRef = true;
            setIsDocumentLoaded(true);
            connectionErrorReporter.clear();
        };
        const handleConnect = () => {
            if (disposed) {
                return;
            }
            const disconnectedFor = lastDisconnectTimeRef.current
                ? Date.now() - lastDisconnectTimeRef.current
                : 0;
            if (disconnectedFor > STALE_TAB_THRESHOLD_MS) {
                console.warn(`[Stale Tab] Client was disconnected for ${Math.round(disconnectedFor / 60000)} minutes. ` +
                    `Forcing fresh reload to prevent data corruption.`);
                lastDisconnectTimeRef.current = null;
                if (ydocRef.current && !ydocRef.current.isDestroyed) {
                    ydocRef.current.destroy();
                }
                ydocRef.current = createLiveYDoc();
                setContentLoaded(false);
                setContentLoadGeneration(prev => prev + 1);
                return;
            }
            lastDisconnectTimeRef.current = null;
            setIsConnected(true);
            connectionErrorReporter.clear();
        };
        const handleDisconnect = () => {
            lastDisconnectTimeRef.current = Date.now();
            setIsConnected(false);
        };
        const handleStatus = (event) => {
            if (event.status === 'connected') {
                setIsConnected(true);
                connectionErrorReporter.clear();
            }
            else if (event.status === 'disconnected') {
                setIsConnected(false);
            }
        };
        const handleError = (event) => {
            const errorMessage = event.reason || event.message || 'Connection error';
            connectionErrorReporter.report(errorMessage);
            setIsConnected(false);
        };
        const handleClose = (event) => {
            if (event.event && event.event.code !== 1000) {
                const errorMessage = event.event.reason || `Connection closed (code: ${event.event.code})`;
                connectionErrorReporter.report(errorMessage);
            }
            setIsConnected(false);
        };
        const handleStateless = ({ payload }) => {
            if (payload === 'document-restored') {
                markDocumentRestoredForReload(documentId);
                window.location.reload();
                return;
            }
            onStatelessRef.current?.(payload);
        };
        newProvider.on('synced', handleSynced);
        newProvider.on('connect', handleConnect);
        newProvider.on('disconnect', handleDisconnect);
        newProvider.on('status', handleStatus);
        newProvider.on('error', handleError);
        newProvider.on('close', handleClose);
        newProvider.on('stateless', handleStateless);
        const timeoutId = setTimeout(() => {
            if (!loadedRef) {
                loadedRef = true;
                setIsDocumentLoaded(true);
            }
        }, 2000);
        return () => {
            disposed = true;
            clearTimeout(timeoutId);
            connectionErrorReporter.dispose();
            newProvider.off('synced', handleSynced);
            newProvider.off('connect', handleConnect);
            newProvider.off('disconnect', handleDisconnect);
            newProvider.off('status', handleStatus);
            newProvider.off('error', handleError);
            newProvider.off('close', handleClose);
            newProvider.off('stateless', handleStateless);
            newProvider.destroy();
            if (providerRef.current === newProvider) {
                providerRef.current = null;
            }
            setProvider((current) => (current === newProvider ? null : current));
        };
    }, [token, masterKey, documentId, websocketUrl, contentLoaded]);
    const cursorUser = React.useMemo(() => {
        const rawColor = userName ? (userColor || generateColorFromString(userName)) : '#958DF1';
        const backgroundColor = normalizeColorToHex(rawColor);
        const textColor = getTextColorForBackground(backgroundColor);
        const user = userName ? {
            name: userName,
            color: backgroundColor,
        } : {
            name: 'Anonymous',
            color: backgroundColor,
        };
        if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--collaboration-caret-text-color', textColor);
        }
        return user;
    }, [userName, userColor]);
    const previousDocumentIdRef = useRef(undefined);
    const editorConfig = React.useMemo(() => {
        const currentYdoc = ydocRef.current;
        if (!currentYdoc) {
            return {
                extensions: [
                    ...createBaseExtensions(headingExtension, getEnableListsSetting),
                    ...(entityMentionExtension ? [entityMentionExtension] : []),
                    ...(extraExtensions ?? []),
                ],
                editable: false,
            };
        }
        const extensions = [
            ...createBaseExtensions(headingExtension, getEnableListsSetting),
            ...(entityMentionExtension ? [entityMentionExtension] : []),
            ...(extraExtensions ?? []),
        ];
        if (getPlaceholderText) {
            const placeholderText = getPlaceholderText();
            if (placeholderText) {
                extensions.push(Placeholder.configure({
                    placeholder: placeholderText,
                    emptyEditorClass: 'is-editor-empty',
                    showOnlyWhenEditable: true,
                    showOnlyCurrent: false,
                }));
            }
        }
        extensions.push(Collaboration.configure({
            document: currentYdoc,
            field: 'default',
        }));
        if (provider && userName) {
            extensions.push(CollaborationCaret.configure({
                provider: provider,
                user: cursorUser,
            }));
        }
        extensions.push(Extension.create({
            name: 'undoRedoKeymap',
            addKeyboardShortcuts() {
                return {
                    'Mod-z': () => this.editor.commands.undo(),
                    'Mod-Shift-z': () => this.editor.commands.redo(),
                    'Mod-y': () => this.editor.commands.redo(),
                    'Mod-Shift-x': () => this.editor.commands.toggleStrike(),
                };
            },
        }), HighlightColor, SuggestionInsertMarkers, HyphenToDash.configure({
            getSetting: getReplaceHyphensWithDashesSetting,
        }), AutomaticReplacements.configure({
            getSetting: getAutomaticReplacementsSetting || (() => []),
        }), SearchHighlightExtension);
        if (getCollapseConsecutiveSpacesSetting) {
            extensions.push(CollapseSpaces.configure({
                getSetting: getCollapseConsecutiveSpacesSetting,
            }));
        }
        if (getEnableScriptModeSetting) {
            extensions.push(ScriptMode.configure({
                getSetting: getEnableScriptModeSetting,
            }));
        }
        if (getScriptMentionItems && getEnableScriptModeSetting) {
            extensions.push(configureScriptMention(getScriptMentionItems, () => Boolean(getEnableScriptModeSetting()), getPreferEntityMentions, () => (documentId && documentId !== 'new' ? documentId : undefined)));
        }
        if (handleImageFiles && typeof window !== 'undefined') {
            extensions.push(FileHandler.configure({
                handleImageFiles,
            }));
        }
        return {
            extensions,
            editable,
            editorProps: {
                attributes: {
                    class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[400px]',
                },
                clipboardTextSerializer: (slice) => {
                    const raw = slice.content.textBetween(0, slice.content.size, '\n', '\n');
                    const normalized = raw.replace(/\r\n/g, '\n').replace(/\n{2,}/g, '\n');
                    return normalized.replace(/\n+$/g, '\n');
                },
            },
            immediatelyRender: false,
        };
    }, [provider, cursorUser, editorKey, userName, documentId, ydoc?.clientID, getReplaceHyphensWithDashesSetting, getCollapseConsecutiveSpacesSetting, getAutomaticReplacementsSetting, getEnableListsSetting, getEnableScriptModeSetting, getScriptMentionItems, getIsScriptModeActive, getPreferEntityMentions, getPlaceholderText, headingExtension, entityMentionExtension, extraExtensions, editable]);
    const editor = useTipTapEditor(editorConfig, [
        documentId,
        editorKey,
        provider,
        ydoc?.clientID,
    ]);
    useEffect(() => {
        if (documentId !== previousDocumentIdRef.current) {
            previousDocumentIdRef.current = documentId;
            setEditorKey(prev => prev + 1);
        }
    }, [documentId]);
    useEffect(() => {
        if (editor && !editor.isDestroyed && editor.extensionManager) {
            editor.setEditable(editable);
        }
    }, [editor, editable]);
    useEffect(() => {
        if (!editor || editor.isDestroyed || !editor.extensionManager || !provider) {
            return;
        }
        const hasCursorExtension = editor.extensionManager.extensions.some((ext) => ext.name === 'collaborationCaret');
        if (!hasCursorExtension || typeof editor.commands.updateUser !== 'function') {
            return;
        }
        if (userName) {
            editor.commands.updateUser(cursorUser);
        }
    }, [editor, provider, userName, cursorUser]);
    useEffect(() => {
        if (!editor || editor.isDestroyed || !editor.extensionManager) {
            return undefined;
        }
        const extensionNames = editor.extensionManager.extensions.map(ext => ext.name);
        const hasCursorExtension = extensionNames.includes('collaborationCaret');
        if (provider && hasCursorExtension) {
            missingCaretRetryRef.current = false;
            if (typeof document !== 'undefined') {
                const updateLabelColors = () => {
                    if (editor.isDestroyed) {
                        return;
                    }
                    let labels;
                    try {
                        labels = editor.view.dom.getElementsByClassName(COLLABORATION_CARET_LABEL_CLASS);
                    }
                    catch {
                        return;
                    }
                    if (labels.length === 0) {
                        return;
                    }
                    for (let i = 0; i < labels.length; i += 1) {
                        const labelElement = labels[i];
                        let bgColor = labelElement.style.backgroundColor;
                        if (!bgColor || bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
                            const computedBgColor = window.getComputedStyle(labelElement).backgroundColor;
                            if (computedBgColor && computedBgColor !== 'rgba(0, 0, 0, 0)' && computedBgColor !== 'transparent') {
                                bgColor = computedBgColor;
                            }
                            else {
                                const caretElement = labelElement.parentElement?.querySelector('.collaboration-caret__caret');
                                if (caretElement) {
                                    const caretBorderColor = window.getComputedStyle(caretElement).borderLeftColor;
                                    if (caretBorderColor && caretBorderColor !== 'rgba(0, 0, 0, 0)' && caretBorderColor !== 'transparent') {
                                        bgColor = caretBorderColor;
                                        labelElement.style.backgroundColor = caretBorderColor;
                                    }
                                }
                            }
                        }
                        if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
                            const textColor = getTextColorForBackground(bgColor);
                            labelElement.style.color = textColor;
                        }
                    }
                };
                let caretLabelRafId = 0;
                const scheduleUpdateLabelColors = () => {
                    if (caretLabelRafId !== 0) {
                        return;
                    }
                    caretLabelRafId = window.requestAnimationFrame(() => {
                        caretLabelRafId = 0;
                        updateLabelColors();
                    });
                };
                updateLabelColors();
                const observer = new MutationObserver((mutations) => {
                    if (mutationsAffectCollaborationCaretLabels(mutations)) {
                        scheduleUpdateLabelColors();
                    }
                });
                observer.observe(editor.view.dom, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
                const intervalId = setInterval(updateLabelColors, 500);
                return () => {
                    if (caretLabelRafId !== 0) {
                        window.cancelAnimationFrame(caretLabelRafId);
                    }
                    observer.disconnect();
                    clearInterval(intervalId);
                };
            }
        }
        else if (provider && !hasCursorExtension && userName && !missingCaretRetryRef.current) {
            missingCaretRetryRef.current = true;
            setEditorKey(prev => prev + 1);
        }
        return undefined;
    }, [editor, provider, editorKey, userName, cursorUser]);
    return { editor, ydoc, isDocumentLoaded, isConnected, connectionError };
};
//# sourceMappingURL=use-collaborative-editor.js.map